import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { MessageSquare, Video, Film, Rocket, ArrowRight } from "lucide-react";
import { useLang } from "./LangContext";

const icons = [MessageSquare, Video, Film, Rocket];

const iconColors = [
  { bg: "bg-[#73B5E2]", ring: "ring-[#73B5E2]/20" },
  { bg: "bg-[#5AA0D0]", ring: "ring-[#5AA0D0]/20" },
  { bg: "bg-[#4A8FC0]", ring: "ring-[#4A8FC0]/20" },
  { bg: "bg-[#3A7EB0]", ring: "ring-[#3A7EB0]/20" },
];

export default function Process() {
  const { t } = useLang();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section id="processus" data-testid="process-section" className="py-20 md:py-32 bg-[#F8FAFC] overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-20"
        >
          <span className="text-[#73B5E2] font-medium text-sm uppercase tracking-widest">
            {t.process.label}
          </span>
          <h2
            data-testid="process-title"
            className="mt-3 text-3xl sm:text-4xl font-bold text-slate-900"
            style={{ fontFamily: 'Outfit' }}
          >
            {t.process.title}
          </h2>
        </motion.div>

        {/* Timeline */}
        <div className="relative">
          {/* Connecting line (desktop) */}
          <motion.div
            initial={{ scaleX: 0 }}
            animate={isInView ? { scaleX: 1 } : {}}
            transition={{ duration: 1.2, delay: 0.3, ease: "easeOut" }}
            className="hidden md:block absolute top-[52px] left-[12%] right-[12%] h-[2px] bg-gradient-to-r from-[#73B5E2]/30 via-[#73B5E2] to-[#73B5E2]/30 origin-left z-0"
          />

          <div className="grid md:grid-cols-4 gap-8 md:gap-6 relative z-10">
            {t.process.steps.map((step, i) => {
              const Icon = icons[i];
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 60 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.7, delay: 0.2 + i * 0.2, type: "spring", stiffness: 100 }}
                  className="flex flex-col items-center text-center group"
                  data-testid={`process-step-${i}`}
                >
                  {/* Animated icon circle */}
                  <motion.div
                    initial={{ scale: 0, rotate: -180 }}
                    animate={isInView ? { scale: 1, rotate: 0 } : {}}
                    transition={{ duration: 0.6, delay: 0.4 + i * 0.2, type: "spring", stiffness: 200 }}
                    className="relative mb-6"
                  >
                    {/* Pulsing ring */}
                    <motion.div
                      animate={isInView ? { scale: [1, 1.3, 1], opacity: [0.3, 0, 0.3] } : {}}
                      transition={{ duration: 2.5, delay: 0.8 + i * 0.2, repeat: Infinity, ease: "easeInOut" }}
                      className={`absolute inset-0 rounded-full ${iconColors[i].bg} opacity-30`}
                    />
                    {/* Outer ring */}
                    <div className={`w-[104px] h-[104px] rounded-full ring-4 ${iconColors[i].ring} bg-white flex items-center justify-center shadow-lg group-hover:shadow-xl transition-shadow duration-300`}>
                      {/* Inner colored circle */}
                      <motion.div
                        whileHover={{ scale: 1.1, rotate: 10 }}
                        className={`w-20 h-20 rounded-full ${iconColors[i].bg} flex items-center justify-center relative`}
                      >
                        <Icon className="w-8 h-8 text-white" strokeWidth={1.8} />
                        {/* Step number badge */}
                        <motion.span
                          initial={{ scale: 0 }}
                          animate={isInView ? { scale: 1 } : {}}
                          transition={{ delay: 0.7 + i * 0.2, type: "spring", stiffness: 400 }}
                          className="absolute -top-1 -right-1 w-7 h-7 rounded-full bg-white text-[#73B5E2] text-xs font-bold flex items-center justify-center shadow-md border-2 border-[#73B5E2]/20"
                        >
                          {i + 1}
                        </motion.span>
                      </motion.div>
                    </div>
                  </motion.div>

                  {/* Arrow between steps (mobile) */}
                  {i < 3 && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={isInView ? { opacity: 1 } : {}}
                      transition={{ delay: 0.6 + i * 0.2 }}
                      className="md:hidden mb-4"
                    >
                      <ArrowRight className="w-5 h-5 text-[#73B5E2]/40 rotate-90" />
                    </motion.div>
                  )}

                  {/* Title */}
                  <motion.h3
                    initial={{ opacity: 0 }}
                    animate={isInView ? { opacity: 1 } : {}}
                    transition={{ delay: 0.6 + i * 0.2 }}
                    className="text-lg font-semibold text-slate-900 mb-2 group-hover:text-[#73B5E2] transition-colors duration-300"
                    style={{ fontFamily: 'Outfit' }}
                  >
                    {step.title}
                  </motion.h3>

                  {/* Description */}
                  <motion.p
                    initial={{ opacity: 0, y: 10 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ delay: 0.7 + i * 0.2, duration: 0.5 }}
                    className="text-slate-500 text-sm leading-relaxed max-w-[220px]"
                  >
                    {step.desc}
                  </motion.p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
